import sanitize from 'sanitize-html';

// Rich text must be sanitized on both the server and client before being injected.
// sanitize-html runs identically in Node and the browser, so SSR and hydration stay consistent.
// Keep HTML formatting, images, and links, but strip scripts, event handlers, and unsafe URLs.
const DEFAULT_ALLOWED_TAGS = [...sanitize.defaults.allowedTags, 'img'];
// Server-rendered links (e.g. feed descriptions) carry a title attribute, which sanitize-html strips by default.
const DEFAULT_ALLOWED_ATTRIBUTES = { ...sanitize.defaults.allowedAttributes, a: ['href', 'name', 'target', 'title'] };

type SanitizeHtmlConfig = {
  ALLOWED_TAGS?: string[];
  ADD_ATTR?: string[];
  /** Opens every link in a new tab, without giving the new page access to this one. */
  OPEN_LINKS_IN_NEW_TAB?: boolean;
};

const sanitizeHtml = (html: string, config?: SanitizeHtmlConfig): string => {
  const allowedAttributes = config?.OPEN_LINKS_IN_NEW_TAB
    ? { ...DEFAULT_ALLOWED_ATTRIBUTES, a: [...DEFAULT_ALLOWED_ATTRIBUTES.a, 'rel'] }
    : DEFAULT_ALLOWED_ATTRIBUTES;

  return sanitize(html, {
    allowedTags: config?.ALLOWED_TAGS ?? DEFAULT_ALLOWED_TAGS,
    allowedAttributes: config?.ADD_ATTR ? { ...allowedAttributes, '*': config.ADD_ATTR } : allowedAttributes,
    parseStyleAttributes: false,
    ...(config?.OPEN_LINKS_IN_NEW_TAB && {
      transformTags: { a: sanitize.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' }) }
    })
  });
};

export default sanitizeHtml;
